/**
 * Инструмент маршрутизации OSRM на MapLibre: клики A/B (route) или origin+destinations (table),
 * overlay линии/маркеров, Esc снимает режим.
 */

import type { Map as MapLibreMap, MapMouseEvent, GeoJSONSource } from "maplibre-gl";

export type RouteToolMode = "route" | "nearest";

export type RoutePoint = { lon: number; lat: number };

export type RouteOverlayState = {
  points: RoutePoint[];
  line: GeoJSON.LineString | null;
};

const SOURCE_ID = "gpml-osrm-route";
const LINE_LAYER = "gpml-osrm-route-line";
const POINTS_LAYER = "gpml-osrm-route-points";

type Options = {
  onPointsChange?: (points: RoutePoint[]) => void;
  onMapClickPoint?: (point: RoutePoint, points: RoutePoint[]) => void;
  onExit?: () => void;
};

export class MapLibreRouteTool {
  private map: MapLibreMap;
  private active = false;
  private mode: RouteToolMode = "route";
  private points: RoutePoint[] = [];
  private line: GeoJSON.LineString | null = null;
  private boundClick: (e: MapMouseEvent) => void;
  private boundKeyDown: (e: KeyboardEvent) => void;
  private opts: Options;

  constructor(map: MapLibreMap, opts: Options = {}) {
    this.map = map;
    this.opts = opts;
    this.boundClick = (e) => this.handleClick(e);
    this.boundKeyDown = (e) => this.handleKey(e);
  }

  isActive(): boolean {
    return this.active;
  }

  getPoints(): RoutePoint[] {
    return [...this.points];
  }

  setMode(mode: RouteToolMode): void {
    this.mode = mode;
  }

  activate(mode: RouteToolMode = "route"): void {
    this.mode = mode;
    if (this.active) {
      this.ensureLayers();
      this.render();
      return;
    }
    this.active = true;
    this.ensureLayers();
    this.map.getCanvas().style.cursor = "crosshair";
    this.map.on("click", this.boundClick);
    window.addEventListener("keydown", this.boundKeyDown);
    this.render();
  }

  deactivate(clear = true): void {
    if (!this.active && clear) {
      this.clearState();
      this.removeLayers();
      return;
    }
    if (!this.active) return;
    this.active = false;
    this.map.getCanvas().style.cursor = "";
    this.map.off("click", this.boundClick);
    window.removeEventListener("keydown", this.boundKeyDown);
    if (clear) {
      this.clearState();
      this.removeLayers();
    }
  }

  setRouteLine(geometry: GeoJSON.LineString | null): void {
    this.line = geometry;
    this.render();
  }

  resetPoints(): void {
    this.points = [];
    this.line = null;
    this.opts.onPointsChange?.([]);
    this.render();
  }

  private clearState(): void {
    this.points = [];
    this.line = null;
    this.opts.onPointsChange?.([]);
  }

  private handleKey(e: KeyboardEvent): void {
    if (e.key === "Escape" && this.active) {
      e.preventDefault();
      e.stopPropagation();
      this.deactivate(true);
      this.opts.onExit?.();
    }
  }

  private handleClick(e: MapMouseEvent): void {
    if (!this.active) return;
    const { lng, lat } = e.lngLat;
    const point: RoutePoint = { lon: lng, lat };
    if (this.mode === "route") {
      if (this.points.length >= 2) {
        this.points = [point];
        this.line = null;
      } else {
        this.points = [...this.points, point];
      }
    } else {
      this.points = [...this.points, point];
    }
    this.opts.onPointsChange?.(this.getPoints());
    this.opts.onMapClickPoint?.(point, this.getPoints());
    this.render();
  }

  private ensureLayers(): void {
    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, {
        type: "geojson",
        data: emptyFc(),
      });
    }
    if (!this.map.getLayer(LINE_LAYER)) {
      this.map.addLayer({
        id: LINE_LAYER,
        type: "line",
        source: SOURCE_ID,
        filter: ["==", ["get", "kind"], "line"],
        paint: {
          "line-color": "#2563eb",
          "line-width": 4,
          "line-opacity": 0.9,
        },
      });
    }
    if (!this.map.getLayer(POINTS_LAYER)) {
      this.map.addLayer({
        id: POINTS_LAYER,
        type: "circle",
        source: SOURCE_ID,
        filter: ["==", ["get", "kind"], "point"],
        paint: {
          "circle-radius": 7,
          "circle-color": [
            "match",
            ["get", "role"],
            "A",
            "#16a34a",
            "B",
            "#dc2626",
            "#f59e0b",
          ],
          "circle-stroke-width": 2,
          "circle-stroke-color": "#ffffff",
        },
      });
    }
  }

  private removeLayers(): void {
    try {
      if (this.map.getLayer(POINTS_LAYER)) this.map.removeLayer(POINTS_LAYER);
      if (this.map.getLayer(LINE_LAYER)) this.map.removeLayer(LINE_LAYER);
      if (this.map.getSource(SOURCE_ID)) this.map.removeSource(SOURCE_ID);
    } catch {
      /* style may be gone */
    }
  }

  private render(): void {
    if (!this.map.getSource(SOURCE_ID)) {
      if (this.active) this.ensureLayers();
      else return;
    }
    const features: GeoJSON.Feature[] = this.points.map((p, i) => {
      let role = "dest";
      if (i === 0) role = "A";
      else if (this.mode === "route" && i === 1) role = "B";
      return {
        type: "Feature",
        properties: { kind: "point", role, index: i },
        geometry: { type: "Point", coordinates: [p.lon, p.lat] },
      };
    });
    if (this.line) {
      features.push({
        type: "Feature",
        properties: { kind: "line" },
        geometry: this.line,
      });
    }
    const src = this.map.getSource(SOURCE_ID) as GeoJSONSource | undefined;
    src?.setData({ type: "FeatureCollection", features });
  }
}

function emptyFc(): GeoJSON.FeatureCollection {
  return { type: "FeatureCollection", features: [] };
}
